/**
 * CorporateBRSRDashboard
 *
 * Tenant-wide rollup of BRSR principle data across all studies in a period.
 * View modes:
 *   - merged    : single latest-updated value per indicator
 *   - per_site  : per-site list per indicator (drill-down)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Building2, RefreshCw } from 'lucide-react';
import CorporatePeriodSelector from './CorporatePeriodSelector';

const API = process.env.REACT_APP_BACKEND_URL;

const CorporateBRSRDashboard = () => {
  const [periodId, setPeriodId] = useState(null);
  const [view, setView] = useState('merged');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [data, setData] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!periodId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [dashRes, dataRes] = await Promise.all([
        axios.get(`${API}/api/brsr/corporate/dashboard?period_id=${periodId}`, { headers }),
        axios.get(`${API}/api/brsr/corporate/data?period_id=${periodId}&view=${view}`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setData(dataRes.data);
    } catch (err) {
      console.error('Corporate BRSR fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [periodId, view]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">Corporate BRSR Rollup</h2>
        </div>
        <div className="flex items-center gap-3">
          <CorporatePeriodSelector value={periodId} onChange={setPeriodId} />
          <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setView('merged')}
              className={`px-3 py-2 text-sm ${view === 'merged' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Merged
            </button>
            <button
              onClick={() => setView('per_site')}
              className={`px-3 py-2 text-sm ${view === 'per_site' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Per Site
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      )}

      {!loading && dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Studies in period" value={dashboard.study_count} />
          <StatCard label="Indicators filled" value={dashboard.stats?.filled_indicators ?? '--'} />
          <StatCard
            label="Completion"
            value={`${dashboard.stats?.completion_rate?.toFixed?.(1) ?? '0'}%`}
          />
        </div>
      )}

      {!loading && data && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Indicators ({data.indicators?.length || 0})
          </h3>
          {(!data.indicators || data.indicators.length === 0) ? (
            <p className="text-sm text-gray-500">No BRSR data found for this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2">Principle</th>
                    <th className="text-left px-3 py-2">Indicator</th>
                    <th className="text-left px-3 py-2">Response</th>
                    {view === 'per_site' && <th className="text-left px-3 py-2">Sites</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.indicators.map((ind, idx) => (
                    <tr key={`${ind.principle_id}-${ind.indicator_id}-${idx}`} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-700">{ind.principle_id}</td>
                      <td className="px-3 py-2 text-gray-600">{ind.indicator_text || ind.indicator_id}</td>
                      <td className="px-3 py-2 text-gray-900 max-w-md truncate">
                        {view === 'per_site'
                          ? `${ind.responses?.length || 0} site response(s)`
                          : (ind.response || '—')}
                      </td>
                      {view === 'per_site' && (
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {(ind.responses || []).map((r, i) => (
                            <div key={i}>
                              <strong>{r.site_name || r.site_id || 'Unknown site'}:</strong>{' '}
                              {r.value || '—'}
                            </div>
                          ))}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
  </div>
);

export default CorporateBRSRDashboard;
